import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, TextField, CircularProgress } from '@mui/material';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';

const EditChapterModal = ({ open, onClose, chapter, onChapterUpdated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useThemeStyles();

  useEffect(() => {
    if (open) {
      setName(chapter?.name || '');
      setDescription(chapter?.description || '');
      setError('');
    }
  }, [open, chapter]);

  const handleClose = () => {
    if (loading) return;
    setError('');
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Chapter name is required.');
      return;
    }

    const chapterId = chapter?.id || chapter?._id;
    if (!chapterId) {
      setError('Unable to determine chapter identifier.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
      };

      const { data, error: requestError } = await axiosWrapper({
        method: 'put',
        path: `chapters/${chapterId}`,
        data: payload,
      });

      if (requestError) {
        throw requestError;
      }

      const updatedChapter = data?.chapter || {
        ...chapter,
        ...payload,
      };

      onChapterUpdated(updatedChapter);
      setLoading(false);
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to update chapter. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Edit Chapter"
      width={420}
      keepMounted
      showCloseButton
      ariaLabelledby="edit-chapter-modal-title"
      ariaDescribedby="edit-chapter-modal-description"
      contentSx={{ gap: 2 }}
    >
      {error && (
        <Typography sx={{ textAlign: 'center', color: colors?.semantic?.error ?? 'error.main' }}>
          {error}
        </Typography>
      )}

      <TextField
        label="Chapter Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        disabled={loading}
        placeholder="Enter chapter title"
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          input: { color: colors?.text?.primary ?? 'text.primary' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
        }}
      />

      <TextField
        label="Chapter Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        disabled={loading}
        multiline
        minRows={3}
        placeholder="Describe the chapter"
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          '& .MuiInputBase-input': { color: colors?.text?.primary ?? 'text.primary' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            minWidth: 110,
            px: 2.5,
            py: 0.75,
            color: colors?.text?.secondary ?? 'text.secondary',
            borderColor: colors?.border?.subtle ?? 'divider',
            '&:hover': {
              color: colors?.text?.primary ?? 'text.primary',
              borderColor: colors?.border?.primary ?? 'primary.main',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
          sx={{
            minWidth: 120,
            px: 2.5,
            py: 0.75,
            backgroundColor: colors?.icon?.primary ?? colors?.primary?.main ?? 'primary.main',
            color: colors?.primary?.contrastText ?? '#ffffff',
            '&:hover': {
              backgroundColor: colors?.icon?.secondary ?? colors?.primary?.main ?? 'primary.main',
            },
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </ReusableModal>
  );
};

EditChapterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chapter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  onChapterUpdated: PropTypes.func.isRequired,
};

EditChapterModal.defaultProps = {
  chapter: null,
};

export default EditChapterModal;
